import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Botao } from './Botao';
import { juntarClasses } from './classes';
import { Esqueleto } from './EstadoCarregando';
import { EstadoVazio } from './EstadoVazio';
import { IconeOrdenacao } from './icones';
import { alternarMarcacao, alternarTodas, podarMarcacao } from './selecao';
import type { Densidade } from './tipos';

export type AlinhamentoColuna = 'esquerda' | 'centro' | 'direita';
export type DirecaoDeOrdenacao = 'crescente' | 'decrescente';

export interface ColunaTabela<T> {
  /** Identificador estável — chave do React e valor de `aoOrdenar`. */
  chave: string;
  titulo: ReactNode;
  /**
   * Coluna de valor: alinha à direita e aplica números tabulares, para os
   * dígitos alinharem em coluna e a diferença de ordem de grandeza saltar aos
   * olhos. É o alinhamento correto de qualquer número comparável.
   */
  numerica?: boolean;
  alinhamento?: AlinhamentoColuna;
  /** CSS de largura mínima, ex.: `'12rem'`. Evita coluna espremida na rolagem. */
  larguraMinima?: string;
  /**
   * A coluna fica do tamanho do conteúdo (ou da `larguraMinima`), e a sobra da
   * largura da tabela vai para as outras. Para a coluna de ações e as de texto
   * curto no fim da linha, que senão ganhariam um vão vazio — a lixeira longe
   * do que ela apaga.
   */
  encolher?: boolean;
  /** Cabeçalho só para leitor de tela — coluna de ações, por exemplo. */
  tituloOculto?: boolean;
  ordenavel?: boolean;
  celula: (linha: T, indice: number) => ReactNode;
}

/**
 * Marcar várias linhas para agir de uma vez — excluir, levar ao CRM.
 *
 * A tabela ganha uma coluna de caixas, com "marcar todas" no cabeçalho e
 * Shift+clique para um intervalo, e — só quando há linha marcada — a barra com
 * as ações, presa ao pé da tela enquanto a lista rola. Ação em lote que aparece
 * sem nada marcado convida a um clique que não faz nada.
 *
 * Marca-se o que está na tela: a linha que sai (excluída, fora do filtro, em
 * outra página) deixa a seleção.
 */
export interface SelecaoDaTabela<T> {
  /** As ações para as linhas marcadas — botões pequenos. `limpar` desmarca tudo. */
  acoes: (marcadas: readonly T[], limpar: () => void) => ReactNode;
  /**
   * A linha que não pode ser marcada diz por quê: a caixa fica desabilitada,
   * com o motivo. Oferecer a caixa e recusar a ação depois seria pior.
   */
  bloqueio?: (linha: T) => string | null;
  /** Como o leitor de tela anuncia a caixa: "Selecionar {nome}". */
  nomeDaLinha?: (linha: T) => string;
  /** O que a lista lista, para a contagem da barra: "3 empresas selecionadas". */
  substantivo?: { singular: string; plural: string; feminino?: boolean };
}

export interface PropsTabela<T> {
  colunas: readonly ColunaTabela<T>[];
  dados: readonly T[];
  /** Chave estável por linha. Índice só se a lista nunca reordenar. */
  chaveDaLinha: (linha: T, indice: number) => string;
  /** Descrição da tabela. Vira `<caption>` e nomeia a região rolável. */
  legenda: string;
  legendaVisivel?: boolean;
  densidade?: Densidade;
  /** Altura máxima da área rolável (ex.: `'30rem'`). Ativa o cabeçalho fixo. */
  alturaMaxima?: string;
  ordenadaPor?: string;
  direcao?: DirecaoDeOrdenacao;
  aoOrdenar?: (chave: string) => void;
  carregando?: boolean;
  /** Conteúdo quando não há linhas. Sem isto, um estado vazio padrão. */
  vazio?: ReactNode;
  aoClicarNaLinha?: (linha: T) => void;
  linhaDestacada?: (linha: T) => boolean;
  /** Uma ou mais `<tr>` de totais, fixas no rodapé da rolagem. */
  rodape?: ReactNode;
  /**
   * `chave` da coluna que identifica a linha (competência, cliente, contrato).
   * Ela é renderizada como `<th scope="row">` em vez de `<td>`.
   *
   * Não é detalhe de marcação: numa tabela fiscal de oito colunas, o leitor de
   * tela lê "R$ 8.333,33" sem dizer de qual mês — com o cabeçalho de linha, ele
   * anuncia "mar/2025, valor a pagar, R$ 8.333,33". Sem isso, quem depende de
   * leitor precisa contar colunas de cabeça.
   */
  chaveDeCabecalhoDaLinha?: string;
  /** Marcar linhas para uma ação em lote (`SelecaoDaTabela`). */
  selecao?: SelecaoDaTabela<T>;
  className?: string;
}

/** A coluna das caixas de marcar — interna, antes das colunas de quem usa. */
const COLUNA_DA_SELECAO = '__selecao';

/** O desenho da caixa mora em `estilos/tokens.css` (`.caixa-de-marcar`). */
const CLASSES_DA_CAIXA = 'caixa-de-marcar';

function contagemDaSelecao(
  n: number,
  substantivo: SelecaoDaTabela<unknown>['substantivo'],
): string {
  const s = substantivo ?? { singular: 'item', plural: 'itens' };
  const selecionado = s.feminino === true ? 'selecionada' : 'selecionado';
  return n === 1 ? `1 ${s.singular} ${selecionado}` : `${n} ${s.plural} ${selecionado}s`;
}

/**
 * Altura de linha.
 *
 * `confortavel` é o padrão porque a listagem é de **entidades**, não de números:
 * cada linha é uma empresa com razão social longa, que quebra em duas linhas, e
 * o operador varre a lista procurando um nome. Com `py-2` os nomes encostavam
 * uns nos outros e a varredura ficava cansativa — o sistema antigo usa linhas
 * bem altas justamente por isso.
 *
 * `compacta` continua para tabela de valores por competência, onde caber doze
 * meses na tela sem rolar vale mais que o respiro.
 */
const PREENCHIMENTO: Readonly<Record<Densidade, string>> = {
  compacta: 'px-4 py-2.5',
  confortavel: 'px-5 py-4',
};

function alinhamentoDe<T>(coluna: ColunaTabela<T>): string {
  const alvo: AlinhamentoColuna =
    coluna.alinhamento ?? (coluna.numerica === true ? 'direita' : 'esquerda');
  if (alvo === 'direita') return 'text-right';
  if (alvo === 'centro') return 'text-center';
  return 'text-left';
}

/**
 * Tabela de dados.
 *
 * Três decisões que vêm da natureza da ferramenta:
 *
 * 1. **A rolagem horizontal é da tabela, não da página.** A área rolável é um
 *    `region` focável por teclado (`tabIndex={0}`) — quem não usa mouse precisa
 *    conseguir rolar o A4 de dados de 12 colunas.
 * 2. **Cabeçalho fixo** ao passar `alturaMaxima`: a coluna "Valor" sem título
 *    visível no meio de 60 linhas de SPED é como se não existisse.
 * 3. **`border-separate`** em vez de `collapse`: com `collapse`, as bordas do
 *    cabeçalho fixo somem durante a rolagem em vários navegadores.
 */
export function Tabela<T>({
  colunas,
  dados,
  chaveDaLinha,
  legenda,
  legendaVisivel = false,
  densidade = 'confortavel',
  alturaMaxima,
  ordenadaPor,
  direcao = 'crescente',
  aoOrdenar,
  carregando = false,
  vazio,
  aoClicarNaLinha,
  linhaDestacada,
  rodape,
  chaveDeCabecalhoDaLinha,
  selecao,
  className,
}: PropsTabela<T>): JSX.Element {
  const preenchimento = PREENCHIMENTO[densidade];
  const estiloDoContainer: CSSProperties | undefined =
    alturaMaxima === undefined ? undefined : { maxHeight: alturaMaxima };
  const clicavel = aoClicarNaLinha !== undefined;

  // ─── Seleção ─────────────────────────────────────────────────────────────
  const [marcadas, setMarcadas] = useState<ReadonlySet<string>>(() => new Set());
  /** A última caixa clicada — de onde o Shift+clique conta o intervalo. */
  const ancora = useRef<string | null>(null);
  const caixaDeTodas = useRef<HTMLInputElement>(null);

  const linhas = dados.map((linha, indice) => ({
    linha,
    chave: chaveDaLinha(linha, indice),
    bloqueio: selecao?.bloqueio?.(linha) ?? null,
  }));
  const marcaveis = selecao === undefined ? [] : linhas.filter((l) => l.bloqueio === null);
  const chavesMarcaveis = marcaveis.map((l) => l.chave);
  const marcadasNaTela = marcaveis.filter((l) => marcadas.has(l.chave));
  const todas = marcaveis.length > 0 && marcadasNaTela.length === marcaveis.length;
  const algumas = marcadasNaTela.length > 0 && !todas;

  const assinaturaDasMarcaveis = JSON.stringify(chavesMarcaveis);
  useEffect(() => {
    setMarcadas((atual) => podarMarcacao(atual, JSON.parse(assinaturaDasMarcaveis) as string[]));
  }, [assinaturaDasMarcaveis]);

  // "Algumas marcadas" não é atributo de HTML: só a propriedade, pelo DOM.
  useEffect(() => {
    if (caixaDeTodas.current !== null) caixaDeTodas.current.indeterminate = algumas;
  }, [algumas]);

  const limpar = (): void => {
    setMarcadas(new Set());
    ancora.current = null;
  };

  const colunaDaSelecao: ColunaTabela<T> | null =
    selecao === undefined
      ? null
      : {
          chave: COLUNA_DA_SELECAO,
          titulo: (
            <input
              ref={caixaDeTodas}
              type="checkbox"
              checked={todas}
              disabled={marcaveis.length === 0}
              onChange={() => setMarcadas((atual) => alternarTodas(atual, chavesMarcaveis))}
              aria-label="Selecionar todas as linhas da lista"
              className={CLASSES_DA_CAIXA}
            />
          ),
          encolher: true,
          celula: (linha, indice) => {
            const chave = chaveDaLinha(linha, indice);
            const motivo = selecao.bloqueio?.(linha) ?? null;
            const nome = selecao.nomeDaLinha?.(linha) ?? 'esta linha';
            return (
              // O motivo fica no invólucro: caixa desabilitada não mostra dica.
              <span title={motivo ?? undefined} className="inline-flex">
                <input
                  type="checkbox"
                  checked={motivo === null && marcadas.has(chave)}
                  disabled={motivo !== null}
                  onChange={(evento) => {
                    // O clique chega como `click`: o Shift vem nele, também pelo teclado.
                    const intervalo = (evento.nativeEvent as MouseEvent).shiftKey === true;
                    const de = ancora.current;
                    setMarcadas((atual) =>
                      alternarMarcacao(atual, chavesMarcaveis, chave, { ancora: de, intervalo }),
                    );
                    ancora.current = chave;
                  }}
                  aria-label={motivo === null ? `Selecionar ${nome}` : `${nome}: ${motivo}`}
                  className={CLASSES_DA_CAIXA}
                />
              </span>
            );
          },
        };
  const todasAsColunas = colunaDaSelecao === null ? colunas : [colunaDaSelecao, ...colunas];

  const regiao = (
    <div
      // `region` + `tabIndex` tornam a área rolável alcançável pelo teclado.
      role="region"
      aria-label={legenda}
      tabIndex={0}
      style={estiloDoContainer}
      className={juntarClasses(
        'min-w-0 max-w-full overflow-auto rounded-painel border border-borda bg-superficie-elevada shadow-cartao',
        // Com seleção, a classe vai para o invólucro da tabela com a barra.
        selecao === undefined && className,
      )}
    >
      {/*
        `min-w-max` fazia a tabela crescer até caber a maior célula em uma linha
        só — com razão social de 60 caracteres, as colunas da direita saíam da
        viewport e só apareciam rolando. `w-full` deixa o navegador distribuir a
        largura e quebrar o texto longo, que é o que o sistema antigo faz.
        As `larguraMinima` de cada coluna seguram o piso.
      */}
      <table className="w-full border-separate border-spacing-0 text-left text-sm">
        <caption
          className={juntarClasses(
            legendaVisivel
              ? 'px-4 pb-2 pt-3 text-left text-xs text-conteudo-secundario'
              : 'sr-only',
          )}
        >
          {legenda}
        </caption>

        <thead>
          <tr>
            {todasAsColunas.map((coluna) => {
              const ordenadaPorEsta = ordenadaPor === coluna.chave;
              const podeOrdenar = coluna.ordenavel === true && aoOrdenar !== undefined;
              return (
                <th
                  key={coluna.chave}
                  scope="col"
                  aria-sort={
                    coluna.ordenavel === true
                      ? ordenadaPorEsta
                        ? direcao === 'crescente'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                      : undefined
                  }
                  style={{
                    ...(coluna.larguraMinima === undefined
                      ? {}
                      : { minWidth: coluna.larguraMinima }),
                    // `1%`: na distribuição automática, a coluna pede o mínimo.
                    ...(coluna.encolher === true ? { width: '1%' } : {}),
                  }}
                  className={juntarClasses(
                    // Cabeçalho escuro, como no sistema antigo: separa a coluna
                    // do dado por contraste, não só por peso de fonte, e é o que
                    // dá âncora visual a uma tabela larga.
                    'sticky top-0 z-10 bg-superficie-tabelaTopo text-xs font-medium uppercase tracking-wide text-conteudo-tabelaTopo',
                    preenchimento,
                    alinhamentoDe(coluna),
                  )}
                >
                  {podeOrdenar ? (
                    <button
                      type="button"
                      onClick={() => aoOrdenar(coluna.chave)}
                      className={juntarClasses(
                        'inline-flex items-center gap-1 rounded-card uppercase transition-colors hover:text-conteudo-primario',
                        coluna.tituloOculto === true && 'sr-only',
                      )}
                    >
                      <span>{coluna.titulo}</span>
                      <IconeOrdenacao
                        direcao={ordenadaPorEsta ? direcao : 'nenhuma'}
                        className={ordenadaPorEsta ? 'text-acento-ciano' : ''}
                      />
                    </button>
                  ) : (
                    <span className={coluna.tituloOculto === true ? 'sr-only' : undefined}>
                      {coluna.titulo}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {carregando ? (
            <LinhasEsqueleto colunas={todasAsColunas} preenchimento={preenchimento} />
          ) : dados.length === 0 ? (
            <tr>
              <td colSpan={todasAsColunas.length} className="p-0">
                {vazio ?? (
                  <EstadoVazio
                    titulo="Nenhum resultado"
                    descricao="Não há dados para os filtros selecionados. Ajuste o período ou limpe os filtros."
                    compacto
                    comMoldura={false}
                  />
                )}
              </td>
            </tr>
          ) : (
            dados.map((linha, indice) => {
              const destacada = linhaDestacada?.(linha) === true;
              return (
                <tr
                  key={chaveDaLinha(linha, indice)}
                  onClick={clicavel ? () => aoClicarNaLinha(linha) : undefined}
                  onKeyDown={
                    clicavel
                      ? (evento) => {
                          if (evento.key !== 'Enter' && evento.key !== ' ') return;
                          evento.preventDefault();
                          aoClicarNaLinha(linha);
                        }
                      : undefined
                  }
                  tabIndex={clicavel ? 0 : undefined}
                  className={juntarClasses(
                    'transition-colors',
                    destacada && 'bg-acento-indigo/5',
                    clicavel && 'cursor-pointer hover:bg-superficie-vidro/70',
                  )}
                >
                  {todasAsColunas.map((coluna) => {
                    const Celula = coluna.chave === chaveDeCabecalhoDaLinha ? 'th' : 'td';
                    // Marcar a caixa não abre a linha — nem pelo clique, nem pelo
                    // espaço do teclado, que a linha também escuta.
                    const daSelecao = coluna.chave === COLUNA_DA_SELECAO;
                    return (
                      <Celula
                        key={coluna.chave}
                        scope={Celula === 'th' ? 'row' : undefined}
                        onClick={daSelecao ? (evento) => evento.stopPropagation() : undefined}
                        onKeyDown={daSelecao ? (evento) => evento.stopPropagation() : undefined}
                        className={juntarClasses(
                          'border-b border-borda/60 font-normal text-conteudo-primario',
                          preenchimento,
                          alinhamentoDe(coluna),
                          coluna.numerica === true && 'valor-fiscal',
                        )}
                      >
                        {coluna.celula(linha, indice)}
                      </Celula>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>

        {rodape !== undefined && (
          <tfoot className="sticky bottom-0 z-10 bg-superficie-vidro/90 backdrop-blur-vidro">
            {rodape}
          </tfoot>
        )}
      </table>
    </div>
  );

  if (selecao === undefined) return regiao;

  const contagem = contagemDaSelecao(marcadasNaTela.length, selecao.substantivo);
  return (
    <div className={juntarClasses('flex min-w-0 flex-col gap-3', className)}>
      {regiao}
      {/* Anunciado sempre: a barra entra e sai, e o leitor de tela precisa saber. */}
      <span className="sr-only" aria-live="polite">
        {marcadasNaTela.length > 0 ? contagem : ''}
      </span>
      {marcadasNaTela.length > 0 && (
        <div
          role="region"
          aria-label="Ações para as linhas selecionadas"
          className="sticky bottom-4 z-20 flex flex-wrap items-center justify-between gap-3 rounded-card border border-acento-ciano/40 bg-superficie-elevada px-4 py-3 shadow-cartao"
        >
          <span className="text-sm font-medium text-conteudo-primario">{contagem}</span>
          <span className="flex flex-wrap items-center gap-2">
            <Botao variante="sutil" tamanho="pequeno" onClick={limpar}>
              Limpar seleção
            </Botao>
            {selecao.acoes(
              marcadasNaTela.map((l) => l.linha),
              limpar,
            )}
          </span>
        </div>
      )}
    </div>
  );
}

function LinhasEsqueleto<T>({
  colunas,
  preenchimento,
}: {
  colunas: readonly ColunaTabela<T>[];
  preenchimento: string;
}): JSX.Element {
  return (
    <>
      {Array.from({ length: 5 }, (_, linha) => (
        <tr key={linha}>
          {colunas.map((coluna) => (
            <td
              key={coluna.chave}
              className={juntarClasses('border-b border-borda/60', preenchimento)}
            >
              <Esqueleto
                className={juntarClasses('h-3', coluna.numerica === true ? 'ml-auto w-16' : 'w-32')}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/**
 * Célula de rodapé de totais — mesma tipografia da tabela, peso maior.
 * Use dentro do `rodape`: `<tr><CelulaDeTotal>Total</CelulaDeTotal>…</tr>`
 */
export function CelulaDeTotal({
  children,
  numerica = false,
  densidade = 'confortavel',
  colSpan,
  cabecalhoDaLinha = false,
}: {
  children: ReactNode;
  numerica?: boolean;
  densidade?: Densidade;
  colSpan?: number;
  /**
   * A célula que nomeia a linha de total ("Total do ano") vira `<th scope="row">`.
   * Sem isso o leitor de tela anuncia a soma sem dizer do que ela é soma.
   */
  cabecalhoDaLinha?: boolean;
}): JSX.Element {
  const Celula = cabecalhoDaLinha ? 'th' : 'td';

  return (
    <Celula
      colSpan={colSpan}
      scope={cabecalhoDaLinha ? 'row' : undefined}
      className={juntarClasses(
        'border-t border-borda font-medium text-conteudo-primario',
        PREENCHIMENTO[densidade],
        numerica ? 'valor-fiscal text-right' : 'text-left',
      )}
    >
      {children}
    </Celula>
  );
}
